import React, { useState, useContext } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import axios from '../../../sharedModules/utils/axios.utils';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import { getFieldByKey, getValueLabel } from '../config/emailAttachmentRulesOptions';
import AddEditAttachmentModal, { type AttachmentRule } from './AddEditAttachmentModal';

interface Props {
  open: boolean;
  rules: AttachmentRule[];
  onClose: () => void;
  onRefresh: () => void;
}

const conditionLabel = (rule: AttachmentRule): string => {
  if (rule.attachment_type === 'global') return '—';
  const fieldLabel = getFieldByKey(rule.field_key ?? '')?.label ?? rule.field_key ?? '';
  const valueLabel = getValueLabel(rule.field_key ?? '', rule.trigger_value ?? '');
  return `${fieldLabel} = ${valueLabel}`;
};

const EmailAttachmentsManageModal: React.FC<Props> = ({ open, rules, onClose, onRefresh }) => {
  const { showSuccessSnackBar, showErrorSnackBar } = useContext(SnackBarContext);
  const [addEditOpen, setAddEditOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AttachmentRule | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const handleEdit = (rule: AttachmentRule) => {
    setEditingRule(rule);
    setAddEditOpen(true);
  };

  const handleAdd = () => {
    setEditingRule(null);
    setAddEditOpen(true);
  };

  const handleDelete = async (rule: AttachmentRule) => {
    if (!window.confirm(`Delete "${rule.name}"? This cannot be undone.`)) return;
    setDeletingId(rule.id);
    try {
      await axios.delete(`/home_services/inspection_report_email_attachment_rules/${rule.id}`);
      showSuccessSnackBar?.(`"${rule.name}" deleted.`);
      onRefresh();
    } catch (err) {
      console.error('Failed to delete attachment rule:', err);
      showErrorSnackBar?.('Failed to delete attachment.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Email Attachments
            <Button variant="contained" size="small" onClick={handleAdd}>
              + Add Attachment
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {rules.length === 0 ? (
            <Typography color="textSecondary" sx={{ py: 2 }}>
              No attachment rules configured. Click "Add Attachment" to create one.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>File Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Condition</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.map((rule) => (
                  <TableRow key={rule.id}>
                    <TableCell>{rule.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={rule.attachment_type === 'global' ? 'Global' : 'Conditional'}
                        size="small"
                        color={rule.attachment_type === 'global' ? 'primary' : 'secondary'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {conditionLabel(rule)}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(rule)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(rule)}
                          disabled={deletingId === rule.id}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <AddEditAttachmentModal
        open={addEditOpen}
        rule={editingRule}
        onClose={() => setAddEditOpen(false)}
        onSaved={onRefresh}
      />
    </>
  );
};

export default EmailAttachmentsManageModal;
